/**
 * Resize.jsx
 * Any number of pictures, every size and format they might need.
 *
 * The job it removes: opening an image editor, exporting the same picture at
 * five sizes, remembering which platform wants which, doing that again for the
 * next twenty photographs, and naming a hundred files so you can tell them
 * apart afterwards.
 *
 * Three decisions shape the screen.
 *
 * **Nothing is rendered until you have seen what it will do.** A landscape
 * photograph forced into a Story loses most of its width, and that should be a
 * sentence you read beforehand rather than something you notice after posting.
 *
 * **One render per size, not per placement.** An Instagram Story, a Facebook
 * Story and a TikTok post are all 1080x1920 — so they make one file, and the
 * row says which placements it covers.
 *
 * **The total is stated before you commit to it.** Twenty photographs across
 * six placements is a hundred and twenty renders, which is a real amount of
 * work for a browser; the button says the number so it is never a surprise.
 *
 * All of it happens here. No image service, no cost, nothing uploaded unless
 * you choose to keep it.
 */

import { useEffect, useRef, useState } from 'react';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import NoBrands from '../components/NoBrands.jsx';
import {
  OUTPUT_FORMATS,
  PLATFORMS,
  PRESETS,
  customToPreset,
  dedupe,
  forPlatform,
  loadCustomSizes,
  outputName,
  saveCustomSizes,
} from '../utils/formats.js';
import { FIT_MODES, isReadable, planExact, readImage, renderTo, shortType } from '../utils/image.js';
import { createZip, filesFromDrop } from '../utils/zip.js';
import { filesFromClipboard, readClipboardImages } from '../utils/clipboard.js';
import { Image as ImageIcon, Download, Check, Warning, Upload, Crop, Clipboard } from '../components/Icons.jsx';
import LibraryImagePicker from '../components/LibraryImagePicker.jsx';
import TextOnImage from '../components/TextOnImage.jsx';
import CustomSizes from '../components/CustomSizes.jsx';

/** Bounds, so dropping a Pictures folder is refused rather than attempted. */
const LIMITS = { sources: 100, renders: 400 };

/**
 * What the screen was holding when you last left it.
 *
 * Converting forty pictures takes real time, and until now stepping into the
 * library to check on them threw all of it away — React unmounts the page and
 * the state goes with it. The rendered files are Blobs in this tab's memory,
 * so parking them outside the component keeps them alive across a route
 * change at no cost; only a reload clears them, which is the one case where
 * they genuinely are gone.
 *
 * Module-level rather than context because nothing else has any use for it.
 */
const KEPT = {
  sources: [],
  results: [],
  saved: new Set(),
  settings: null,
};

export function forgetKeptResults() {
  for (const item of [...KEPT.sources, ...KEPT.results]) URL.revokeObjectURL(item.url);
  KEPT.sources = [];
  KEPT.results = [];
  KEPT.saved = new Set();
}

export default function Resize() {
  const { brands, loading, error: brandsError } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();

  const [sources, setSources] = useState(KEPT.sources);
  const [chosen, setChosen] = useState(KEPT.settings?.chosen ?? ['ig-portrait', 'ig-story', 'fb-link']);
  // Sizes typed in by hand. Kept in localStorage rather than in KEPT, because
  // a size somebody needs once they tend to need every week — surviving a
  // reload is the point of them.
  const [customs, setCustoms] = useState(loadCustomSizes);
  const [mime, setMime] = useState(KEPT.settings?.mime ?? 'image/jpeg');
  const [fit, setFit] = useState(KEPT.settings?.fit ?? 'pad');
  const [background, setBackground] = useState(KEPT.settings?.background ?? '#ffffff');

  const [results, setResults] = useState(KEPT.results);
  // Which of them are already in a library, so pressing save twice does not
  // put a second copy of everything there. Keyed by brand as well as by file:
  // the same picture saved into PeekoType is not saved into Archevot, and a
  // tick that forgets which is worse than no tick at all.
  const [saved, setSaved] = useState(() => new Set(KEPT.saved));
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [dropping, setDropping] = useState(0);
  // Choosing pictures that are already in Kreeboom, and putting a hook line on
  // one. Both make ordinary sources, so everything below them is unchanged.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [textSource, setTextSource] = useState(null);

  const fileInput = useRef(null);
  const folderInput = useRef(null);
  // Folder inputs are not in the JSX attribute list, so they are set by hand.
  useEffect(() => {
    if (folderInput.current) {
      folderInput.current.setAttribute('webkitdirectory', '');
      folderInput.current.setAttribute('directory', '');
    }
  }, []);

  // Kept for the next visit. Object URLs are deliberately not revoked on
  // unmount — that is exactly what used to make the thumbnails come back
  // broken — and forgetKeptResults() is the one place that lets them go.
  useEffect(() => {
    KEPT.sources = sources;
    KEPT.results = results;
    KEPT.saved = saved;
    KEPT.settings = { chosen, mime, fit, background };
  }, [sources, results, saved, chosen, mime, fit, background]);

  useEffect(() => {
    saveCustomSizes(customs);
  }, [customs]);

  /** Reads each file far enough to know its shape, and says what it refused. */
  async function add(files) {
    const incoming = Array.from(files ?? []).filter((f) => f.type.startsWith('image/'));
    if (incoming.length === 0) return;

    setError(null);
    setResults([]);
    setSaved(new Set());
    setNote(null);

    const room = LIMITS.sources - sources.length;
    const taking = incoming.slice(0, Math.max(0, room));
    const refused = [];

    const added = [];
    for (const file of taking) {
      if (!isReadable(file.type)) {
        refused.push(`${file.name} is a ${shortType(file.type)}, which a browser cannot read`);
        continue;
      }
      try {
        const { bitmap, width, height } = await readImage(file);
        bitmap.close?.();
        added.push({
          id: `${file.name}-${file.size}-${file.lastModified}`,
          file,
          width,
          height,
          url: URL.createObjectURL(file),
        });
      } catch {
        refused.push(`${file.name} could not be opened`);
      }
    }

    // Dropping the same folder twice should not duplicate everything in it.
    setSources((current) => {
      const seen = new Set(current.map((s) => s.id));
      return [...current, ...added.filter((s) => !seen.has(s.id))];
    });

    const overflow = incoming.length - taking.length;
    if (refused.length || overflow) {
      setNote(
        [
          overflow ? `${overflow} left out — ${LIMITS.sources} pictures is the most at once.` : null,
          refused.length ? `Skipped: ${refused.slice(0, 3).join('; ')}.` : null,
        ]
          .filter(Boolean)
          .join(' '),
      );
    }
  }

  const savedHere = (key) => saved.has(`${brandId}::${key}`);
  const savedCount = results.filter((r) => savedHere(r.key)).length;
  const allSaved = results.length > 0 && savedCount === results.length;

  /** The Paste button — see the note in utils/clipboard.js for why both exist. */
  async function pasteFromClipboard() {
    const { files, reason } = await readClipboardImages();
    if (reason) {
      setError(new Error(reason));
      return;
    }
    setError(null);
    await add(files);
  }

  // A paste arrives on the window, not on an element. Text is ignored, so this
  // never steals a paste meant for a field elsewhere on the page.
  useEffect(() => {
    function onPaste(e) {
      const files = filesFromClipboard(e.clipboardData);
      if (files.length === 0) return;
      e.preventDefault();
      add(files);
    }

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  });

  const presets = chosen.map((key) => PRESETS.find((p) => p.key === key)).filter(Boolean);
  // The placement list counts only placements; the render counts both, and a
  // typed size that happens to match a preset is merged into one file by
  // dedupe exactly as two matching placements are.
  const presetSizes = dedupe(presets);
  const sizes = dedupe([...presets, ...customs.filter((c) => c.on).map(customToPreset)]);
  const totalRenders = sources.length * sizes.length;
  const tooMany = totalRenders > LIMITS.renders;

  async function render() {
    if (sources.length === 0 || sizes.length === 0) return;

    setBusy(true);
    setError(null);
    setNote(null);
    setResults([]);
    setSaved(new Set());

    const made = [];
    let done = 0;

    try {
      for (const source of sources) {
        for (const size of sizes) {
          const name = outputName(source.file.name, size, mime);
          const { file, plan } = await renderTo(source.file, size, {
            fit,
            background,
            mime,
            name,
          });

          made.push({
            key: `${source.id}-${size.width}x${size.height}`,
            name,
            file,
            plan,
            covers: size.covers,
            from: source.file.name,
            url: URL.createObjectURL(file),
            bytes: file.size,
          });

          setProgress({ done: ++done, total: totalRenders, name });
          // Let the browser paint between renders; a hundred canvases in a
          // tight loop freezes the tab and looks like a crash.
          await new Promise((r) => setTimeout(r, 0));
        }
      }
      setResults(made);
    } catch (e) {
      setError(e);
    } finally {
      setProgress(null);
      setBusy(false);
    }
  }

  /** Everything as one archive, built here rather than downloaded one by one. */
  async function downloadAll() {
    setBusy(true);
    setError(null);
    try {
      const entries = await Promise.all(
        results.map(async (r) => ({
          // Grouped by size inside the archive, which is how you use them.
          name: `${r.plan.width}x${r.plan.height}/${r.name}`,
          bytes: new Uint8Array(await r.file.arrayBuffer()),
        })),
      );

      const zip = await createZip(entries, {
        onProgress: (done, total) => setProgress({ done, total, name: 'building the archive' }),
      });

      const url = URL.createObjectURL(zip);
      const link = document.createElement('a');
      link.href = url;
      link.download = `kreeboom-images-${new Date().toISOString().slice(0, 10)}.zip`;
      link.click();
      // Revoked on the next tick: revoking immediately cancels the download in
      // some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setError(e);
    } finally {
      setProgress(null);
      setBusy(false);
    }
  }

  /**
   * Keeps them in the brand's library.
   *
   * One upload failing used to abandon the rest of the batch and leave no
   * record of which had already gone up, so pressing save again duplicated
   * everything that had worked. Each file is now tracked on its own: the ones
   * that land are marked, the ones that fail are named, and a second press
   * retries only what is still missing.
   */
  async function saveAll() {
    const brand = brands.find((b) => b.id === brandId);
    const pending = results.filter((r) => !savedHere(r.key));

    if (pending.length === 0) {
      setNote(`All ${results.length} are already in ${brand?.name ?? 'the'}’s library.`);
      return;
    }

    setBusy(true);
    setError(null);
    setNote(null);

    const landed = new Set(saved);
    const failed = [];
    let done = 0;

    for (const result of pending) {
      try {
        await api.media.upload(brandId, result.file);
        landed.add(`${brandId}::${result.key}`);
      } catch (e) {
        failed.push(`${result.name} (${e.message})`);
      }
      setProgress({ done: ++done, total: pending.length, name: result.name });
    }

    setSaved(landed);
    setProgress(null);
    setBusy(false);

    const count = pending.length - failed.length;
    if (failed.length === 0) {
      setNote(
        `${count} added to ${brand?.name ?? 'the'}’s library, ready to use in a post.`,
      );
    } else {
      setError(
        new Error(
          `${count} of ${pending.length} saved. Failed: ${failed.slice(0, 3).join('; ')}` +
            `${failed.length > 3 ? ` and ${failed.length - 3} more` : ''}. Press save again to retry just those.`,
        ),
      );
    }
  }

  /** Clears the batch and lets the memory go. */
  function startAgain() {
    forgetKeptResults();
    setSources([]);
    setResults([]);
    setSaved(new Set());
    setError(null);
    setNote(null);
  }

  const toggle = (key) =>
    setChosen((c) => (c.includes(key) ? c.filter((k) => k !== key) : [...c, key]));

  const dragProps = {
    onDragEnter: (e) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      setDropping((d) => d + 1);
    },
    onDragOver: (e) => {
      if (e.dataTransfer?.types?.includes('Files')) e.preventDefault();
    },
    onDragLeave: () => setDropping((d) => Math.max(0, d - 1)),
    onDrop: async (e) => {
      e.preventDefault();
      setDropping(0);
      // A dropped folder arrives as an entry to walk, not as a File.
      const found = await filesFromDrop(e.dataTransfer.items, { maxFiles: LIMITS.sources });
      await add(found.length ? found : e.dataTransfer.files);
    },
  };

  if (!loading && brands.length === 0) return <NoBrands error={brandsError} />;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Resize &amp; convert</div>
          <h1>Any number of pictures, every size you need</h1>
          <p>
            Instagram, Facebook, LinkedIn and TikTok, in whichever format you want. One picture or a
            whole folder. It all happens in your browser — nothing is uploaded unless you keep it.
          </p>
        </div>
        <div className="page-head__actions">
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}
      {note && (
        <div className="notice notice--info">
          <Warning />
          <span>{note}</span>
        </div>
      )}

      {progress && (
        <div className="notice notice--info">
          <Check />
          <span>
            {progress.done} of {progress.total} — {progress.name}
          </span>
        </div>
      )}

      {/* ---------- the pictures ---------- */}
      <section className={`card dropzone${dropping > 0 ? ' is-dropping' : ''}`} {...dragProps}>
        <div className="card__head">
          <span className="tile">
            <Upload size={21} />
          </span>
          <div className="card__head-text">
            <h2>
              {sources.length === 0
                ? 'Choose pictures'
                : `${sources.length} picture${sources.length === 1 ? '' : 's'}`}
            </h2>
            <p>
              One, several, or a whole folder. JPEG, PNG, WebP, AVIF or GIF — drag them onto this
              panel and any folders are opened for you.
            </p>
          </div>
          {sources.length > 0 && (
            <div className="row__end">
              <button className="btn btn--sm" onClick={() => setSources([])} disabled={busy}>
                Clear
              </button>
            </div>
          )}
        </div>

        <div className="btn-row">
          <button className="btn" onClick={() => fileInput.current?.click()} disabled={busy}>
            <ImageIcon size={15} /> Choose files
          </button>
          <button className="btn" onClick={() => folderInput.current?.click()} disabled={busy}>
            <Upload size={15} /> Choose a folder
          </button>
          <button
            className="btn"
            onClick={() => setLibraryOpen(true)}
            disabled={busy || !brandId}
            title={
              brandId
                ? 'Pictures already saved in this brand’s library'
                : 'Choose a brand first'
            }
          >
            <ImageIcon size={15} /> From the library
          </button>
          <button
            className="btn"
            onClick={pasteFromClipboard}
            disabled={busy}
            title="Paste a picture you have copied"
          >
            <Clipboard size={15} /> Paste
          </button>

          <input
            ref={fileInput}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
            hidden
            onChange={(e) => add(e.target.files)}
          />
          {/* webkitdirectory is set in an effect: React does not pass it through. */}
          <input ref={folderInput} type="file" multiple hidden onChange={(e) => add(e.target.files)} />
        </div>

        {sources.length > 0 && (
          <div className="library" style={{ marginTop: 16 }}>
            {sources.slice(0, 24).map((source) => (
              <div className="lib-item" key={source.id}>
                <img src={source.url} alt="" />
                <div className="lib-item__meta">
                  <span>
                    {source.width}×{source.height}
                  </span>
                  <button
                    className="lib-item__act"
                    disabled={busy}
                    onClick={() => setTextSource(source)}
                    title="Put a hook line on this picture"
                  >
                    Add text
                  </button>
                  <button
                    className="lib-item__delete"
                    disabled={busy}
                    onClick={() => setSources((c) => c.filter((s) => s.id !== source.id))}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            {sources.length > 24 && (
              <p className="hint">…and {sources.length - 24} more.</p>
            )}
          </div>
        )}
      </section>

      {sources.length > 0 && (
        <>
          <Sizes
            sources={sources}
            chosen={chosen}
            toggle={toggle}
            setChosen={setChosen}
            fit={fit}
            mime={mime}
            sizes={presetSizes}
          />

          {/* ---------- a size of your own ---------- */}
          <CustomSizes
            sources={sources}
            customs={customs}
            setCustoms={setCustoms}
            fit={fit}
            mime={mime}
            busy={busy}
          />

          {/* ---------- format and framing ---------- */}
          <section className="card">
            <div className="card__head">
              <div className="card__head-text">
                <h2>Format and framing</h2>
              </div>
            </div>

            <div className="field">
              <label>Save as</label>
              <div className="btn-row">
                {Object.entries(OUTPUT_FORMATS).map(([type, format]) => (
                  <button
                    key={type}
                    className={`btn btn--sm ${mime === type ? 'btn--primary' : ''}`}
                    onClick={() => setMime(type)}
                  >
                    {format.label}
                  </button>
                ))}
              </div>
              <p className="hint">{OUTPUT_FORMATS[mime].note}</p>

              {mime === 'image/jpeg' && sources.some((s) => s.file.type === 'image/png') && (
                <div className="notice notice--info notice--compact">
                  <Warning />
                  <span>
                    JPEG has no transparency. Any transparent PNG among these becomes the colour
                    chosen below.
                  </span>
                </div>
              )}
            </div>

            <div className="field">
              <label>When the shape does not match</label>
              <div className="btn-row">
                {Object.entries(FIT_MODES).map(([key, m]) => (
                  <button
                    key={key}
                    className={`btn btn--sm ${fit === key ? 'btn--primary' : ''}`}
                    onClick={() => setFit(key)}
                  >
                    {m.label}
                  </button>
                ))}
                {(fit === 'pad' || mime === 'image/jpeg') && (
                  <input
                    type="color"
                    value={background}
                    onChange={(e) => setBackground(e.target.value)}
                    title="Background colour"
                    style={{ width: 44, padding: 2 }}
                  />
                )}
              </div>
              <p className="hint">{FIT_MODES[fit].hint}</p>
            </div>

            {tooMany && (
              <div className="notice notice--error">
                <Warning />
                <span>
                  {sources.length} pictures across {sizes.length} sizes is {totalRenders} files —
                  over the {LIMITS.renders} this does at once. Choose fewer sizes, or fewer
                  pictures.
                </span>
              </div>
            )}

            <div className="btn-row">
              <button
                className="btn btn--primary"
                onClick={render}
                disabled={busy || sizes.length === 0 || tooMany}
              >
                <Crop size={15} />
                {busy
                  ? 'Working…'
                  : `Make ${totalRenders} file${totalRenders === 1 ? '' : 's'}`}
              </button>
              <span className="hint">
                {sources.length} picture{sources.length === 1 ? '' : 's'} × {sizes.length} size
                {sizes.length === 1 ? '' : 's'}
              </span>
            </div>
          </section>
        </>
      )}

      {/* ---------- what came out ---------- */}
      {results.length > 0 && (
        <section className="card">
          <div className="card__head">
            <span className="tile tile--ok">
              <ImageIcon size={21} />
            </span>
            <div className="card__head-text">
              <h2>{results.length} ready</h2>
              <p>
                Download them all as one archive, foldered by size — or keep them in{' '}
                {brands.find((b) => b.id === brandId)?.name}’s library. They stay on this
                screen while you look elsewhere, until you reload the page.
              </p>
            </div>
            <div className="row__end">
              <button className="btn btn--primary" onClick={downloadAll} disabled={busy}>
                <Download size={15} /> Download all
              </button>
              {/* The brand is named on the button because it is the thing that
                  goes wrong: the picture saves perfectly, into the other
                  brand's library, and you go looking for it in this one. */}
              <button className="btn" onClick={saveAll} disabled={busy || !brandId}>
                {allSaved ? (
                  <>
                    <Check size={15} /> Saved to {brands.find((b) => b.id === brandId)?.name}
                  </>
                ) : (
                  `Save ${savedCount > 0 ? `the remaining ${results.length - savedCount}` : `all ${results.length}`} to ${brands.find((b) => b.id === brandId)?.name ?? 'the library'}`
                )}
              </button>
              <button className="btn" onClick={startAgain} disabled={busy}>
                Start again
              </button>
            </div>
          </div>

          <div className="library">
            {results.slice(0, 60).map((result) => (
              <div className="lib-item" key={result.key}>
                <img src={result.url} alt="" />
                <div className="lib-item__meta">
                  <span>
                    {savedHere(result.key) && <Check size={12} />} {result.plan.width}×
                    {result.plan.height} · {Math.round(result.bytes / 1024)}KB
                  </span>
                  <a className="lib-item__delete" href={result.url} download={result.name}>
                    <Download size={13} /> Save
                  </a>
                </div>
                <p className="hint" style={{ padding: '0 4px 6px' }}>
                  {result.from}
                </p>
              </div>
            ))}
          </div>

          {results.length > 60 && (
            <p className="hint">
              Showing 60 of {results.length}. The archive has all of them.
            </p>
          )}
        </section>
      )}

      {libraryOpen && (
        <LibraryImagePicker
          brandId={brandId}
          onClose={() => setLibraryOpen(false)}
          onChosen={(files, failed) => {
            setLibraryOpen(false);
            add(files);
            if (failed?.length) {
              setNote(`${failed.length} could not be fetched from the library.`);
            }
          }}
        />
      )}

      {textSource && (
        <TextOnImage
          source={textSource}
          onClose={() => setTextSource(null)}
          onDone={(file) => {
            setTextSource(null);
            add([file]);
            setNote('Added as a new picture. The one you started from is untouched.');
          }}
        />
      )}
    </>
  );
}

/**
 * The size picker.
 *
 * Split out because it does its own arithmetic per preset — what this
 * particular batch would lose at that shape — and inlining that made the main
 * component hard to follow.
 */
function Sizes({ sources, chosen, toggle, setChosen, fit, mime, sizes }) {
  return (
    <section className="card">
      <div className="card__head">
        <div className="card__head-text">
          <h2>
            {sizes.length} size{sizes.length === 1 ? '' : 's'} from {chosen.length} placement
            {chosen.length === 1 ? '' : 's'}
          </h2>
          <p>
            Placements that share a size make one file. The note says what each is for, and what
            this batch would lose at that shape.
          </p>
        </div>
      </div>

      {Object.entries(PLATFORMS)
        .sort((a, b) => a[1].order - b[1].order)
        .map(([platform, meta]) => (
          <div className="evg-group" key={platform}>
            <h3 className="evg-group__head">
              {meta.label}
              <button
                className="linkish"
                onClick={() => {
                  const keys = forPlatform(platform).map((p) => p.key);
                  const allOn = keys.every((k) => chosen.includes(k));
                  setChosen((c) =>
                    allOn ? c.filter((k) => !keys.includes(k)) : [...new Set([...c, ...keys])],
                  );
                }}
              >
                {forPlatform(platform).every((p) => chosen.includes(p.key)) ? 'none' : 'all'}
              </button>
            </h3>

            <div className="rows">
              {forPlatform(platform).map((preset) => {
                const on = chosen.includes(preset.key);

                // How many of this batch would be badly cropped or enlarged at
                // this shape — a count, because "one of forty" and "all forty"
                // are very different warnings.
                const plans = on
                  ? sources.map((s) =>
                      planExact({ width: s.width, height: s.height, type: s.file.type }, preset, {
                        fit,
                        mime,
                      }),
                    )
                  : [];
                const cropped = plans.filter((p) => p.heavyCrop).length;
                const upscaled = plans.filter((p) => p.upscaled).length;

                return (
                  <label className={`row row--pick${on ? ' is-picked' : ''}`} key={preset.key}>
                    <input type="checkbox" checked={on} onChange={() => toggle(preset.key)} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="row__label">
                        {preset.label}
                        <span className="badge">
                          {preset.width}×{preset.height}
                        </span>
                        <span className="badge">{preset.shape}</span>
                      </div>
                      <div className="row__sub">{preset.note}</div>

                      {cropped > 0 && (
                        <div className="row__sub delta--down">
                          {cropped === sources.length
                            ? 'Every picture'
                            : `${cropped} of ${sources.length}`}{' '}
                          would lose a lot to cropping at this shape.
                        </div>
                      )}
                      {upscaled > 0 && (
                        <div className="row__sub delta--down">
                          {upscaled === sources.length
                            ? 'Every picture'
                            : `${upscaled} of ${sources.length}`}{' '}
                          would be enlarged, so will look softer.
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
    </section>
  );
}
